import { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {MdDashboard,} from "react-icons/md";
import {
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBook,
  FaMapMarkedAlt,
  FaUsers,
  FaCamera,
  FaDollarSign,
  FaTimes,
  FaBars,
} from "react-icons/fa";
import { FaRegFolderClosed } from "react-icons/fa6";
import { HiDocumentReport } from "react-icons/hi";
import Source from "./SourceIMG.png";
import InitialsAvatar from "./InitialsAvatar.jsx";
import { AnalysisRunProvider } from "../context/AnalysisRunContext.jsx";
import AnalysisProgressModal from "./AnalysisProgressModal.jsx";
import AnalysisMinimizedIndicator from "./AnalysisMinimizedIndicator.jsx";

const LayoutShell = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const userRole = sessionStorage.getItem("userRole");
  const [collapsed, setCollapsed] = useState(false);
  // Below `lg` the sidebar is an off-canvas drawer instead of a static column,
  // so narrow screens give the whole width to the page content.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [user, setUser] = useState({ fullName: "", email: "", profile_picture: null });
  const [enabledPlays, setEnabledPlays] = useState([]);
  const [profileCardOpen, setProfileCardOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const profileCardRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
  const PROFIL_URL = `${BASE_URL}/user/profile/`;
  const PICTURE_URL = `${BASE_URL}/user/profile/picture/`;

  const menuItems = [
    { name: "Dashboard", path: "/admin/dashboard", icon: <MdDashboard size={32} /> },
    { name: "Projects", path: "/OrgChart", icon: <FaRegFolderClosed size={32} /> },
    { name: "Shared with me", path: "/shared-with-me", icon: <FaUsers size={32} /> },
    { name: "Reports", path: "/reports", icon: <HiDocumentReport size={32} /> },
    { name: "Part Costs", path: "/part-costs", icon: <FaDollarSign size={32} /> },
    { name: "Drawing Intelligence", path: "/drawing-intelligence", icon: <FaMapMarkedAlt size={32} /> },
    { name: "How To", path: "/training", icon: <FaBook size={32} /> },
  ];

  const systemAdminMenuItems = [
    { name: "Dashboard", path: "/system-admin", icon: <MdDashboard size={32} /> },
    { name: "Projects", path: "/OrgChart", icon: <FaRegFolderClosed size={32} /> },
    { name: "Shared with me", path: "/shared-with-me", icon: <FaUsers size={32} /> },
    { name: "Reports", path: "/reports", icon: <HiDocumentReport size={32} /> },
    { name: "Part Costs", path: "/part-costs", icon: <FaDollarSign size={32} /> },
    { name: "Drawing Intelligence", path: "/drawing-intelligence", icon: <FaMapMarkedAlt size={32} /> },
    { name: "How To", path: "/training", icon: <FaBook size={32} /> },
  ];

  const handleMenuClick = (item) => {
    if (item.name === "PlayBook") {
      console.log("PlayBook button clicked!");
    }
    setMobileNavOpen(false);
    history.push(item.path);
  }
  const userMenuItems = [
    { name: "Projects", path: "/OrgChart", icon: <FaRegFolderClosed size={32} /> },
    { name: "Shared with me", path: "/shared-with-me", icon: <FaUsers size={32} /> },
    { name: "Reports", path: "/reports", icon: <HiDocumentReport size={32} /> },
    { name: "Part Costs", path: "/part-costs", icon: <FaDollarSign size={32} /> },
    { name: "Drawing Intelligence", path: "/drawing-intelligence", icon: <FaMapMarkedAlt size={32} /> },
    { name: "How To", path: "/training", icon: <FaBook size={32} /> },
  ];

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(PROFIL_URL, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${sessionStorage.getItem("userToken")}`
          }
        });
        if (!response.ok) throw new Error("Failed to fetch profile");
        const data = await response.json();
        setUser({ fullName: data.fullName, email: data.email, profile_picture: data.profile_picture });
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchUserProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close the profile card on outside click or Escape.
  useEffect(() => {
    if (!profileCardOpen) return;

    const handlePointerDown = (e) => {
      if (profileCardRef.current && !profileCardRef.current.contains(e.target)) {
        setProfileCardOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setProfileCardOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [profileCardOpen])

  // Close the mobile drawer on Escape, and whenever the viewport grows back to
  // the `lg` breakpoint where the sidebar is a static column again.
  useEffect(() => {
    if (!mobileNavOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileNavOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileNavOpen])

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const formData = new FormData();
    formData.append('profile_picture', file);
    setUploading(true);
    try {
      const res = await fetch(PICTURE_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionStorage.getItem("userToken")}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.msg || 'Upload failed');
        return;
      }
      const data = await res.json();
      setUser(prev => ({ ...prev, profile_picture: data.profile_picture }));
      toast.success('Profile picture updated successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const fetchEnabledPlays = async () => {
      try {
        const token = sessionStorage.getItem("userToken");
        const res = await fetch(`${BASE_URL}/user/admin/org/plays/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEnabledPlays(data.enabled_plays || []);
        }
      } catch (err) {
        // fail-open: default to empty (Drawing Intelligence hidden until loaded)
      }
    };
    fetchEnabledPlays();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredMenuItems = (() => {
    let items;
    switch (userRole) {
      case "Admin":
      case "Useradmin":
        items = menuItems;
        break;
      case "SystemAdmin":
      case "System Admin":
        items = systemAdminMenuItems;
        break;
      default:
        items = userMenuItems;
        break;
    }
    // Gate "Drawing Intelligence" on supply_chain_v1 being enabled
    if (!enabledPlays.includes('supply_chain_v1')) {
      items = items.filter(i => i.name !== 'Drawing Intelligence');
    }
    return items;
  })();

  return (
    <div className="w-full h-screen bg-gray-100 flex relative overflow-hidden">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} />

      {/* Mobile top bar — only rendered below `lg`, where the sidebar is hidden. */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-[#1E5045] text-[#FECF8B] flex items-center gap-3 px-4 shadow-md">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="p-2 -ml-2 rounded hover:bg-white/10 transition"
          aria-label="Open navigation menu"
        >
          <FaBars size={20} />
        </button>
        <img src={Source} alt="Logo" className="h-7 w-auto object-contain" />
      </header>

      {/* Backdrop behind the drawer */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`${collapsed ? "lg:w-36" : "lg:w-56"} w-64 flex-shrink-0
          bg-[#1E5045] text-[#FECF8B] p-4 flex flex-col justify-between shadow-lg
          transition-transform duration-300 lg:transition-all
          fixed inset-y-0 left-0 z-50 overflow-y-auto
          lg:static lg:z-auto lg:translate-x-0
          ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`} >
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-8">
            {!collapsed && (
              <img
                src={Source}
                alt="Logo"
                className="header-logo w-full max-w-[180px] h-auto object-contain"
              />
            )}
            {/* Collapse toggle is desktop-only; on mobile the drawer closes instead. */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:block text-[#1E5045] bg-[#FECF8B] p-2 rounded-s-lg flex-shrink-0"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <FaChevronRight size={20} />
              ) : (
                <FaChevronLeft size={20} />
              )}
            </button>
            <button
              onClick={() => setMobileNavOpen(false)}
              className="lg:hidden text-[#1E5045] bg-[#FECF8B] p-2 rounded-lg flex-shrink-0"
              aria-label="Close navigation menu"
            >
              <FaTimes size={18} />
            </button>
          </div>
          <div className="space-y-2">
            {(filteredMenuItems || []).map((item) => {
              const active = location.pathname === item.path;

              return (
                <div key={item.name} className="relative group">
                  <button
                    className={`flex items-center w-full px-3 py-2 font-semibold transition duration-200 rounded text-left
            ${active
                        ? "bg-[#FECF8B] text-[#1E5045] hover:bg-[#FFD77D]"
                        : "hover:text-orange-400"
                      }`}
                    onClick={() => handleMenuClick(item)}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {/* The drawer is always full-width, so labels stay visible
                        below `lg` even when the desktop sidebar is collapsed. */}
                    <span className={`ml-3 ${collapsed ? "lg:hidden" : ""}`}>{item.name}</span>
                  </button>

                  {collapsed && (
                    <span
                      className="hidden lg:block absolute left-14 top-1/2 transform -translate-y-1/2
            bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap"
                    >
                      {item.name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Logout */}
        <div className="relative group">
          <div className=" flex justify-center items-center p-2 w-full  mb-2  ">
            <div className="relative" ref={profileCardRef}>
              <div className="relative group/avatar">
                <InitialsAvatar
                  name={user.fullName}
                  email={user.email}
                  src={user.profile_picture}
                  size={40}
                  className="cursor-pointer"
                  title="View profile"
                  onClick={() => setProfileCardOpen(open => !open)}
                />
                <div
                  className="absolute inset-0 rounded-full bg-black bg-opacity-30 opacity-0 group-hover/avatar:opacity-100 transition cursor-pointer"
                  onClick={() => setProfileCardOpen(open => !open)}
                />
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePictureUpload}
              />

              {/* Expanded profile card */}
              {profileCardOpen && (
                <div
                  className="absolute bottom-full mb-3 left-0 lg:bottom-0 lg:mb-0 lg:left-full lg:ml-3
                    z-50 w-56 sm:w-64 max-w-[calc(100vw-3rem)] bg-white rounded-xl shadow-2xl
                    border border-gray-200 p-5 flex flex-col items-center text-center"
                >
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 transition"
                    onClick={() => setProfileCardOpen(false)}
                    aria-label="Close profile card"
                  >
                    <FaTimes size={14} />
                  </button>

                  <div className="relative group/large">
                    <InitialsAvatar
                      name={user.fullName}
                      email={user.email}
                      src={user.profile_picture}
                      size={112}
                      className="shadow-md"
                    />
                    <button
                      className="absolute inset-0 flex items-center justify-center rounded-full bg-black
                        bg-opacity-45 text-white opacity-0 group-hover/large:opacity-100 transition
                        disabled:opacity-100 disabled:cursor-wait"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      title="Change profile picture"
                    >
                      {uploading
                        ? <span className="text-xs font-medium">Uploading…</span>
                        : <FaCamera size={24} />}
                    </button>
                  </div>

                  <p className="mt-3 font-semibold text-gray-900 first-letter:uppercase break-words w-full">
                    {user.fullName}
                  </p>
                  <p className="text-sm text-gray-500 break-all w-full">{user.email}</p>

                  <button
                    className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg
                      bg-[#1E5045] text-[#FECF8B] text-sm font-semibold
                      hover:bg-[#174037] transition disabled:opacity-60 disabled:cursor-wait"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <FaCamera size={14} />
                    {uploading
                      ? "Uploading…"
                      : user.profile_picture ? "Change photo" : "Upload photo"}
                  </button>
                </div>
              )}
            </div>
            <div className={`ml-1 min-w-0 space-y-1 justify-center items-center ${collapsed ? "lg:hidden" : ""}`}>
              <p className="font-semibold text-white first-letter:uppercase truncate">{user.fullName}</p>
              <p className="text-sm text-gray-300 truncate max-w-[150px] overflow-hidden text-ellipsis">
                {user.email}
              </p>
            </div>
          </div>
          <button
            className="flex items-center px-3 py-2 hover:text-red-400 text-lg font-semibold rounded"
            onClick={() => history.push("/login")}
          >
            <FaSignOutAlt size={28} className="mr-3 flex-shrink-0" />
            <span className={collapsed ? "lg:hidden" : ""}>Logout</span>
          </button>

          {/* Tooltip for logout */}
          {collapsed && (
            <span
              className="hidden lg:block absolute left-14 top-1/2 transform -translate-y-1/2
              bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none"
            >
              Logout
            </span>
          )}
        </div>
      </aside>

      {/* `min-w-0` lets this flex child shrink below its content width instead of
          forcing the page wider than the viewport on small screens. */}
      <main className="flex-1 min-w-0 flex flex-col h-screen overflow-y-auto overflow-x-hidden
        pt-20 px-4 pb-4 lg:pt-6 lg:px-6 lg:pb-6">
        {children}
      </main>

      {/* Live-analysis UI lives here, outside the routed <main>, so it keeps
          rendering (and its run keeps polling) no matter which page is open. */}
      <AnalysisProgressModal />
      <AnalysisMinimizedIndicator />
    </div>
  );
};

// The provider wraps the shell so the run state sits above the private-route
// <Switch> in index.js — navigating between pages no longer unmounts the
// SSE stream or the status polling loop.
const Layout = ({ children }) => (
  <AnalysisRunProvider>
    <LayoutShell>{children}</LayoutShell>
  </AnalysisRunProvider>
);

export default Layout;
